import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { produce } from "immer";
import {
  GetAllTicketsResponse,
  SlaTrackingData,
  TicketListBaseObject,
  TicketObjectResponse,
  UpdateTicketPriorityResponse,
  UpdateTicketStatusResponse,
} from "shared";
import { queryKeys } from "../helpers";
import { getWorstStatus, mapNonNullValuesByKey } from "utilz/helpers";

export const useTicketListCache = () => {
  const queryClient = useQueryClient();

  const ticketListCache = () => {
    const queryCache = queryClient.getQueryCache();
    const ticketCache = queryCache.findAll({
      queryKey: queryKeys.tickets.list(),
      type: "active",
      exact: false,
    });
    return ticketCache;
  };

  const updateTicketListItemCache = (ticketListItem: TicketListBaseObject) => {
    const ticketCache = ticketListCache();

    ticketCache.forEach((cache) => {
      queryClient.setQueryData(cache.queryKey, (oldData: any) => {
        if (!oldData || !oldData.result) return oldData;

        return produce(oldData, (draft: GetAllTicketsResponse) => {
          const ticketIndex = draft.result.findIndex(
            (cachedTicket: TicketObjectResponse) =>
              cachedTicket.mapping_id === ticketListItem.mapping_id
          );
          if (ticketIndex !== -1) {
            try {
              draft.result[ticketIndex] = mapNonNullValuesByKey({
                currentObj: oldData.result[ticketIndex],
                incomingObj: ticketListItem,
              });
            } catch (error) {
              console.log(error);
            }
          }
        });
      });
    });
  };

  const updateTicketSlaCache = useCallback(
    (ticketId: string, slaFields: Partial<SlaTrackingData>) => {
      const caches = ticketListCache();

      caches.forEach((cache) => {
        queryClient.setQueryData(cache.queryKey, (oldData: unknown) => {
          const data = oldData as GetAllTicketsResponse | undefined;
          if (!data?.result) return oldData;

          return produce(data, (draft) => {
            const ticket = draft.result.find(
              (t) => t.mapping_id === ticketId,
            );
            if (!ticket) return;

            const existing: SlaTrackingData = ticket.sla_tracking ?? {
              overallStatus: "pending",
              firstResponseStatus: "pending",
              firstResponseTargetMinutes: null,
              firstResponseElapsedMinutes: null,
              resolutionStatus: "pending",
              resolutionTargetMinutes: null,
              resolutionElapsedMinutes: null,
              escalationLevelTriggered: null,
            };

            const merged = { ...existing, ...slaFields };
            merged.overallStatus = getWorstStatus(
              merged.firstResponseStatus,
              merged.resolutionStatus,
            );

            ticket.sla_tracking = merged;
          });
        });
      });
    },
    [queryClient],
  );

  const updateTicketAgentStatusCache = useCallback(
    (ticketId: string, agentType: string, newStatus: string) => {
      const caches = ticketListCache();

      caches.forEach((cache) => {
        queryClient.setQueryData(cache.queryKey, (oldData: unknown) => {
          const data = oldData as GetAllTicketsResponse | undefined;
          if (!data?.result) return oldData;

          return produce(data, (draft) => {
            const ticket = draft.result.find(
              (t) => t.mapping_id === ticketId,
            );
            if (!ticket) return;

            const existingAgent = ticket.ticket_agents?.find(
              (a) => a.agent_type === agentType,
            );
            if (existingAgent) {
              existingAgent.status = newStatus;
            } else {
              if (!ticket.ticket_agents) ticket.ticket_agents = [];
              ticket.ticket_agents.push({
                agent_type: agentType,
                status: newStatus,
              });
            }
          });
        });
      });
    },
    [queryClient],
  );

  const updateTicketSentimentCache = useCallback(
    (ticketId: string, sentiment: "positive" | "neutral" | "negative") => {
      const caches = ticketListCache();

      caches.forEach((cache) => {
        queryClient.setQueryData(cache.queryKey, (oldData: unknown) => {
          const data = oldData as GetAllTicketsResponse | undefined;
          if (!data?.result) return oldData;

          return produce(data, (draft) => {
            const ticket = draft.result.find(
              (t) => t.mapping_id === ticketId,
            );
            if (!ticket) return;

            ticket.current_sentiment = sentiment;
          });
        });
      });
    },
    [queryClient],
  );

  return {
    updateTicketListItemCache,
    updateTicketSlaCache,
    updateTicketAgentStatusCache,
    updateTicketSentimentCache,
  };
};
